import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { SelectedAdditionalServicesCard } from "@/features/flights/components/flight-purchase/AdditionalServicesSelection/SelectedAdditionalServicesCard";
import BottomDrawer from "@/features/flights/components/flight-purchase/BottomDrawer";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import type { AdditionalServiceData } from "@/features/flights/types/types";
import {
  getAvailableServices,
  getCheapestMandatoryServices,
  getNotMandatoryFreeOptionServices,
} from "@/features/flights/utils/additionalServices";
import { TravellerSelectionStatusDrawer } from "@/features/travellers/components/TravellerSelectionStatusDrawer";
import TravellersList from "@/features/travellers/components/TravellersList";
import { TravellersSelection } from "@/features/travellers/components/TravellersSelection";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import type {
  Traveller,
  TravellerAgeGroup,
} from "@/features/travellers/types/types";
import { getTravellerTypeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { travelerTypeToTypeCode } from "@/features/travellers/utils/transformTraveler";
import { FlightTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

export const Route = createFileRoute(
  "/_authenticated/flights/_offers/offer/$id/travellers/",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();
  const { searchData } = useFlightSearchDataContext();
  const { getTravellerServices, setTravellerServices, selectedFlightPayload } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));

  const {
    travellerSlots,
    selectedTravellers,
    selectTraveller,
    getTravellerIndex,
  } = useTravellersSelectionContext();

  const areAdditionalServicesEnabled = useFeatureIsOn(
    "additional-services-selection",
  );

  const { data: travellers } = useQuery(travellersQueryOptions());

  const getServicesInfo = useCallback(
    (traveller: Traveller, overrideTravellerIndex?: number) => {
      const availableServices = getAvailableServices({
        traveller,
        travellerIndex:
          overrideTravellerIndex !== undefined
            ? overrideTravellerIndex
            : getTravellerIndex(traveller.id),
        optionalServices: singleFlightDetails?.optionalServices,
        mandatoryServices: singleFlightDetails?.mandatoryServices,
      });

      const selectedServices = getTravellerServices(traveller.id);

      const availableMandatoryServiceCount = availableServices.filter(
        (service) => service.mandatory,
      ).length;

      const availableNotMandatoryFreeOptionServiceCount =
        availableServices.filter(
          (service) =>
            !service.mandatory &&
            service.service.options.some((option) => option.price.amount === 0),
        ).length;

      // null if there are no mandatory services to select from, boolean otherwise
      const hasSelectedAllMandatoryServices =
        availableMandatoryServiceCount > 0
          ? selectedServices.filter((service) => service.mandatory).length ===
            availableMandatoryServiceCount
          : null;

      // null if there are no services with free option to select from, boolean otherwise
      const hasSelectedAllServicesWithFreeOption =
        availableNotMandatoryFreeOptionServiceCount > 0
          ? selectedServices.filter(
              (service) => !service.mandatory && service.freeOption,
            ).length === availableNotMandatoryFreeOptionServiceCount
          : null;

      return {
        availableServices,
        selectedServices,
        selectedServicesCount: selectedServices.length,
        hasSelectedAllMandatoryServices,
        hasSelectedAllServicesWithFreeOption,
      };
    },
    [
      getTravellerIndex,
      getTravellerServices,
      singleFlightDetails?.mandatoryServices,
      singleFlightDetails?.optionalServices,
    ],
  );

  const autoSelectAdditionalServices = (
    traveller: Traveller,
    slotIndex?: number,
  ) => {
    const {
      hasSelectedAllMandatoryServices,
      hasSelectedAllServicesWithFreeOption,
      availableServices,
    } = getServicesInfo(traveller, slotIndex);

    let autoSelectedServices: AdditionalServiceData[] = [];

    // We're selecting the cheapest mandatory services for traveller being selected to streamline the journey
    if (hasSelectedAllMandatoryServices === false) {
      const selectedServices = getCheapestMandatoryServices(availableServices);
      autoSelectedServices = autoSelectedServices.concat(selectedServices);
    }

    // We're selecting the first free options for not mandatory services for traveller being selected to streamline the journey
    if (hasSelectedAllServicesWithFreeOption === false) {
      const selectedServices =
        getNotMandatoryFreeOptionServices(availableServices);
      autoSelectedServices = autoSelectedServices.concat(selectedServices);
    }

    if (autoSelectedServices.length > 0) {
      setTravellerServices(traveller.id, autoSelectedServices);
    }
  };

  const hasSelectedMandatoryServicesForAllTravellers = useMemo(() => {
    if (!areAdditionalServicesEnabled) {
      return true;
    }

    return selectedTravellers.every((traveller) => {
      const servicesInfo = getServicesInfo(traveller);

      // Either all mandatory services are selected, or there are no mandatory services to select from
      return (
        servicesInfo.hasSelectedAllMandatoryServices === null ||
        servicesInfo.hasSelectedAllMandatoryServices === true
      );
    });
  }, [areAdditionalServicesEnabled, selectedTravellers, getServicesInfo]);

  const travelDate = new Date(searchData!.dates.departure);

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  return (
    <>
      <div className="px-4">
        <div className="text-xl font-medium text-text-title">
          {intl.formatMessage({
            defaultMessage: "Enter traveller details",
            description: "Instruction heading above the traveller details form",
          })}
        </div>
        <div className="mb-4 text-sm text-text-tertiary">
          {intl.formatMessage({
            defaultMessage:
              "Tap each traveller below to enter their information",
            description:
              "Instruction to tap each traveller card to enter their information",
          })}
        </div>
        <TravellersSelection
          onSlotClick={(ageGroup, index) => {
            setSelectedSlot({ ageGroup, index });
          }}
          renderAdditionalServiceControls={(traveller) => {
            if (!areAdditionalServicesEnabled) {
              return null;
            }

            const { availableServices, selectedServicesCount } =
              getServicesInfo(traveller);

            if (availableServices.length === 0) {
              return null;
            }

            return (
              <SelectedAdditionalServicesCard
                servicesSelectedCount={selectedServicesCount}
                onClick={() => {
                  navigate({
                    to: "./additional-services",
                    search: {
                      travellerId: traveller.id,
                    },
                  });
                }}
              />
            );
          }}
        />

        <SwipeableDrawer
          hasFullHeight
          heightOffset={24}
          open={!!selectedSlot}
          onClose={() => setSelectedSlot(null)}
          backgroundColor="var(--color-surface-background)"
        >
          <DrawerTopBar
            onCloseClick={() => setSelectedSlot(null)}
            title={
              selectedSlot
                ? intl.formatMessage(
                    {
                      defaultMessage: "Add traveller for {type}",
                      description:
                        "Prefix label when assigning a traveller to a booking slot",
                    },
                    {
                      type: intl.formatMessage(
                        {
                          defaultMessage:
                            "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
                          description:
                            "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
                        },
                        {
                          type: selectedSlot.ageGroup,
                          number: selectedSlot.index + 1,
                        },
                      ),
                    },
                  )
                : null
            }
          />
          {selectedSlot ? (
            <TravellersList
              travellers={(travellers || []).filter(
                (traveller) =>
                  travelerTypeToTypeCode(
                    getTravellerTypeAtTravelDate(
                      traveller.birthDate,
                      travelDate,
                    ),
                  ) === selectedSlot.ageGroup,
              )}
              travelDate={travelDate}
              selectedTravellerId={
                travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
              }
              isSelectionDisabled={(traveller) =>
                selectedTravellers.some((t) => t.id === traveller.id)
              }
              onTravellerSelected={(travellerId) => {
                selectTraveller(selectedSlot.index, travellerId);
                setSelectedSlot(null);

                const traveller = travellers?.find((t) => t.id === travellerId);

                if (!traveller || !areAdditionalServicesEnabled) return;

                autoSelectAdditionalServices(traveller, selectedSlot.index);
              }}
            />
          ) : null}
        </SwipeableDrawer>
      </div>

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <BottomDrawer
        buttonLabel={intl.formatMessage({
          defaultMessage: "Continue",
          description:
            "Primary button to proceed to the next flight booking step",
        })}
        onButtonClick={() => {
          rudderAnalytics.track(
            FlightTrackingEvents.USER_CONTINUED_ON_TRAVELERS,
            {
              passenger_count: selectedTravellers.length,
            },
          );

          navigate({ to: "/flights/offer/$id/summary" });
        }}
        disabled={
          !allTravellersSelected ||
          !hasSelectedMandatoryServicesForAllTravellers ||
          isAdditionalDataLoading
        }
      />
    </>
  );
}
